import { useCallback } from 'react';
import { toast } from 'react-toastify';
import { useLazySearchCallsByPhoneQuery } from '../api/endpoints/apiConfig';
import type { CallSearchRow } from '../api/endpoints/apiConfig';
import {
    AIRCALL_USER_ID_SESSION_KEY,
    applyCallIdFromSearchResult,
} from '../utils/callsIdSessionSync';

function findAnsweredCall(calls: CallSearchRow[]): CallSearchRow | undefined {
    return calls.find((c) => c.status === 'answered');
}

export type LookupActiveAircallCallOptions = {
    successMessage?: string;
    showErrorToasts?: boolean;
};

export function useLookupActiveAircallCall() {
    const [triggerSearchCalls, { isFetching }] = useLazySearchCallsByPhoneQuery();

    const lookupActiveCall = useCallback(
        async (options?: LookupActiveAircallCallOptions): Promise<boolean> => {
            const showErrorToasts = options?.showErrorToasts ?? true;
            const rawUserId = sessionStorage.getItem(AIRCALL_USER_ID_SESSION_KEY)?.trim();
            if (!rawUserId || Number.isNaN(Number(rawUserId))) {
                if (showErrorToasts) {
                    toast.error('Air Call User ID is not available.');
                }
                return false;
            }
            const userIdParam = Number(rawUserId);
            const fromTs = Math.floor(Date.now() / 1000) - 30 * 60;
            try {
                const res = await triggerSearchCalls({
                    from: fromTs,
                    user_id: userIdParam,
                }).unwrap();
                const calls = Array.isArray(res.calls) ? res.calls : [];
                const hit = findAnsweredCall(calls);
                if (hit) {
                    applyCallIdFromSearchResult(hit);
                    toast.success(
                        options?.successMessage ?? 'Air Call call ID found.'
                    );
                    return true;
                }
                if (showErrorToasts) {
                    toast.error(
                        `Error - Air Call call ID is not found for user_id ${userIdParam}`
                    );
                }
                return false;
            } catch {
                if (showErrorToasts) {
                    toast.error(
                        `Error - Air Call call ID is not found for user_id ${userIdParam}`
                    );
                }
                return false;
            }
        },
        [triggerSearchCalls]
    );

    return { lookupActiveCall, isLookupSearching: isFetching };
}
